import type {
    ReactiveDropdownDataSource,
    ReactiveDropdownDataSourceContext,
    ReactiveDropdownDataSourceParams,
} from '../../interfaces/form.interface';
import type { DropdownData, Value } from '../../interfaces/input.interface';

export type ReactiveCatalogSourceName = string;

export type ReactiveParsedCatalogKey<TSource extends ReactiveCatalogSourceName = ReactiveCatalogSourceName> = {
    readonly source: TSource | null;
    readonly primaryCode: string;
    readonly secondaryCode: string;
    readonly parts: readonly string[];
};

export type ReactiveSourceAwareCatalogRow<TSource extends ReactiveCatalogSourceName = ReactiveCatalogSourceName> = {
    readonly source: TSource;
    readonly catalogKey: string;
    readonly catalogCode: string;
};


export type ReactiveCatalogCompositeLoader<
    TSource extends ReactiveCatalogSourceName,
    TRow,
    TResult,
> = {
    readonly source: TSource;
    readonly enabled?: boolean;
    readonly load: () => Promise<readonly TRow[]>;
    readonly map: (row: TRow, source: TSource) => TResult;
    readonly label?: (row: TResult) => unknown;
};

export type ReactiveCatalogDependentLoader<
    TSource extends ReactiveCatalogSourceName,
    TParams,
    TRow,
    TResult,
> = {
    readonly source: TSource;
    readonly load: (params: TParams) => Promise<readonly TRow[]>;
    readonly map: (row: TRow, source: TSource, params: TParams) => TResult;
    readonly filter?: (row: TResult, params: TParams) => boolean;
    readonly label?: (row: TResult) => unknown;
};

export type ReactiveCatalogAliasConfiguration<TSource extends ReactiveCatalogSourceName = ReactiveCatalogSourceName> = {
    readonly source: TSource;
    readonly simpleValues?: readonly unknown[];
    readonly sourceValues?: readonly unknown[];
    readonly primaryValues?: readonly unknown[];
    readonly secondaryValues?: readonly unknown[];
};

export type ReactiveCatalogDropdownDataSourceOptions<TModel = unknown> = Omit<
    Partial<ReactiveDropdownDataSource<TModel>>,
    'endpoint' | 'cacheKey' | 'mapOption' | 'mapResponse'
>;

export type ReactiveCatalogOptionMapper<TModel, TResult> = (
    item: TResult,
    context: ReactiveDropdownDataSourceContext<TModel>,
) => Value<unknown>;

export type ReactiveCatalogCompositeDropdownConfiguration<
    TModel,
    TSource extends ReactiveCatalogSourceName,
    TRow,
    TResult,
> = {
    readonly cacheKey: string;
    readonly sources: readonly ReactiveCatalogCompositeLoader<TSource, TRow, TResult>[];
    readonly mapOption: ReactiveCatalogOptionMapper<TModel, TResult>;
    readonly options?: ReactiveCatalogDropdownDataSourceOptions<TModel>;
};

export type ReactiveCatalogDependentDropdownConfiguration<
    TModel,
    TSource extends ReactiveCatalogSourceName,
    TParams,
    TRow,
    TResult,
> = {
    readonly cacheKey: string;
    readonly dependsOn: readonly string[];
    readonly sources: readonly ReactiveCatalogDependentLoader<TSource, TParams, TRow, TResult>[];
    readonly params: ReactiveDropdownDataSourceParams<TModel>;
    readonly resolveSource: (params: Record<string, unknown>) => TSource | null;
    readonly mapParams: (params: Record<string, unknown>) => TParams | null;
    readonly mapOption: ReactiveCatalogOptionMapper<TModel, TResult>;
    readonly shouldLoad?: (params: TParams) => boolean;
    readonly options?: ReactiveCatalogDropdownDataSourceOptions<TModel>;
};

const CATALOG_KEY_SEPARATOR = ':';
const CATALOG_SOURCE_INDEX = 0;
const CATALOG_PRIMARY_CODE_INDEX = 1;
const CATALOG_SECONDARY_CODE_INDEX = 2;

/** Shared helpers for source-aware catalog dropdowns. */
export class ReactiveCatalogSource {

    public static async loadComposite<
        TSource extends ReactiveCatalogSourceName,
        TRow,
        TResult,
    >(loaders: readonly ReactiveCatalogCompositeLoader<TSource, TRow, TResult>[]): Promise<TResult[]> {
        const enabledLoaders = loaders.filter((loader) => loader.enabled !== false);
        const rows = (await Promise.all(enabledLoaders.map(async (loader): Promise<TResult[]> => {
            const loadedRows = await loader.load();

            return loadedRows.map((row) => loader.map(row, loader.source));
        }))).flat();
        const labelResolver = enabledLoaders.find((loader) => loader.label)?.label;

        return ReactiveCatalogSource.sortRows(rows, labelResolver);
    }

    public static createCompositeDropdownDataSource<
        TModel,
        TSource extends ReactiveCatalogSourceName,
        TRow,
        TResult,
    >(
        configuration: ReactiveCatalogCompositeDropdownConfiguration<TModel, TSource, TRow, TResult>,
    ): ReactiveDropdownDataSource<TModel> {
        return {
            ...configuration.options,
            endpoint: () => ReactiveCatalogSource.loadComposite(configuration.sources),
            cacheKey: configuration.cacheKey,
            mapResponse: (response) => ReactiveCatalogSource.toArrayResponse(response),
            mapOption: (item, context) => configuration.mapOption(item as TResult, context),
        };
    }

    public static createDependentDropdownDataSource<
        TModel,
        TSource extends ReactiveCatalogSourceName,
        TParams,
        TRow,
        TResult,
    >(
        configuration: ReactiveCatalogDependentDropdownConfiguration<TModel, TSource, TParams, TRow, TResult>,
    ): ReactiveDropdownDataSource<TModel> {
        return {
            ...configuration.options,
            endpoint: (params: Record<string, unknown>) => ReactiveCatalogSource.loadConfiguredDependent(configuration, params),
            cacheKey: configuration.cacheKey,
            lazy: configuration.options?.lazy ?? true,
            dependsOn: configuration.dependsOn,
            params: configuration.params,
            mapResponse: (response) => ReactiveCatalogSource.toArrayResponse(response),
            mapOption: (item, context) => configuration.mapOption(item as TResult, context),
        };
    }

    public static async loadDependent<
        TSource extends ReactiveCatalogSourceName,
        TParams,
        TRow,
        TResult,
    >(
        loaders: readonly ReactiveCatalogDependentLoader<TSource, TParams, TRow, TResult>[],
        source: TSource,
        params: TParams,
    ): Promise<TResult[]> {
        const loader = loaders.find((candidate) => candidate.source === source);

        if (!loader) {
            return [];
        }

        const rows = (await loader.load(params))
            .map((row) => loader.map(row, loader.source, params))
            .filter((row) => loader.filter ? loader.filter(row, params) : true);

        return ReactiveCatalogSource.sortRows(rows, loader.label);
    }

    public static buildKey<TSource extends ReactiveCatalogSourceName>(source: TSource, ...parts: readonly unknown[]): string {
        const normalizedParts = parts
            .map((part) => ReactiveCatalogSource.toStringId(part))
            .filter((part) => part !== '');

        return [source, ...normalizedParts].join(CATALOG_KEY_SEPARATOR);
    }

    public static parseKey<TSource extends ReactiveCatalogSourceName>(
        value: unknown,
        sources: readonly TSource[],
    ): ReactiveParsedCatalogKey<TSource> {
        const rawValue = ReactiveCatalogSource.toStringId(value);
        const parts = rawValue.split(CATALOG_KEY_SEPARATOR);
        const source = ReactiveCatalogSource.resolveSource(parts[CATALOG_SOURCE_INDEX], sources, null);

        if (!source) {
            return {
                source: null,
                primaryCode: '',
                secondaryCode: '',
                parts,
            };
        }

        return {
            source,
            primaryCode: ReactiveCatalogSource.toStringId(parts[CATALOG_PRIMARY_CODE_INDEX]),
            secondaryCode: ReactiveCatalogSource.toStringId(parts[CATALOG_SECONDARY_CODE_INDEX]),
            parts,
        };
    }

    public static resolveSource<TSource extends ReactiveCatalogSourceName>(
        value: unknown,
        sources: readonly TSource[],
        fallback: TSource | null,
    ): TSource | null {
        const normalizedValue = ReactiveCatalogSource.toStringId(value);

        return sources.find((source) => source === normalizedValue) ?? fallback;
    }

    public static buildAliases<TSource extends ReactiveCatalogSourceName>(
        configuration: ReactiveCatalogAliasConfiguration<TSource>,
    ): string[] {
        const aliases = new Set<string>();

        ReactiveCatalogSource.addAliasCandidates(aliases, configuration.simpleValues ?? []);
        ReactiveCatalogSource.addSourceAliases(aliases, configuration.source, configuration.sourceValues ?? []);
        ReactiveCatalogSource.addCompositeAliases(
            aliases,
            configuration.source,
            configuration.primaryValues ?? [],
            configuration.secondaryValues ?? [],
        );

        return Array.from(aliases);
    }

    public static resolveFirstValue(...values: readonly unknown[]): string {
        for (const value of values) {
            const normalizedValue = ReactiveCatalogSource.toStringId(value);

            if (normalizedValue !== '') {
                return normalizedValue;
            }
        }

        return '';
    }

    public static resolveFirstAliasValue(...values: readonly unknown[]): string {
        for (const value of values) {
            const candidates = ReactiveCatalogSource.toAliasCandidates(value);

            if (candidates.length > 0) {
                return candidates[0] ?? '';
            }
        }

        return '';
    }

    public static normalizeName(value: unknown): string {
        return ReactiveCatalogSource.toStringId(value)
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .replace(/\s+/g, ' ')
            .toUpperCase();
    }

    public static compareLabels(first: unknown, second: unknown): number {
        return String(first ?? '').localeCompare(String(second ?? ''), 'es', { sensitivity: 'base' });
    }

    public static toStringId(value: unknown): string {
        if (value === null || value === undefined) {
            return '';
        }

        return String(value).trim();
    }

    public static readRecordValue(value: unknown, key: string): unknown {
        return ReactiveCatalogSource.isRecord(value) ? value[key] : null;
    }

    public static upsertDropdownOption(data: DropdownData<unknown>, option: Value<unknown>): void {
        data.values = ReactiveCatalogSource.upsertDropdownOptionInList(data.values, option);
        data.filtered = ReactiveCatalogSource.upsertDropdownOptionInList(data.filtered, option);
    }

    public static getDropdownOptionAliases(option: Value<unknown>): string[] {
        return [option.value, ...(option.valueAliases ?? [])]
            .map((value) => ReactiveCatalogSource.toStringId(value))
            .filter((value) => value !== '');
    }

    private static async loadConfiguredDependent<
        TModel,
        TSource extends ReactiveCatalogSourceName,
        TParams,
        TRow,
        TResult,
    >(
        configuration: ReactiveCatalogDependentDropdownConfiguration<TModel, TSource, TParams, TRow, TResult>,
        params: Record<string, unknown>,
    ): Promise<TResult[]> {
        const source = configuration.resolveSource(params);
        const mappedParams = configuration.mapParams(params);

        if (!source || mappedParams === null || configuration.shouldLoad?.(mappedParams) === false) {
            return [];
        }

        return ReactiveCatalogSource.loadDependent(configuration.sources, source, mappedParams);
    }

    private static toArrayResponse(response: unknown): unknown[] {
        if (Array.isArray(response)) {
            return response;
        }

        if (ReactiveCatalogSource.isDropdownData(response)) {
            return response.values;
        }

        return [];
    }

    private static isDropdownData(value: unknown): value is DropdownData<unknown> {
        return ReactiveCatalogSource.isRecord(value) && Array.isArray(value['values']);
    }

    private static sortRows<TResult>(rows: TResult[], labelResolver: ((row: TResult) => unknown) | undefined): TResult[] {
        if (!labelResolver) {
            return rows;
        }

        return rows.sort((first, second) => ReactiveCatalogSource.compareLabels(labelResolver(first), labelResolver(second)));
    }

    private static isRecord(value: unknown): value is Record<string, unknown> {
        return value !== null && typeof value === 'object' && !Array.isArray(value);
    }

    private static upsertDropdownOptionInList<TOption extends Value<unknown>>(
        options: readonly TOption[],
        option: Value<unknown>,
    ): TOption[] {
        const nextOption = option as TOption;
        const existingIndex = options.findIndex((item) => ReactiveCatalogSource.doDropdownOptionsShareAlias(item, option));

        if (existingIndex < 0) {
            return [nextOption, ...options];
        }

        return options.map((item, index) => index === existingIndex ? nextOption : item);
    }

    private static doDropdownOptionsShareAlias(first: Value<unknown>, second: Value<unknown>): boolean {
        const firstAliases = ReactiveCatalogSource.getDropdownOptionAliases(first);
        const secondAliases = ReactiveCatalogSource.getDropdownOptionAliases(second);
        const sharedAliases = firstAliases.filter((alias) => secondAliases.includes(alias));

        if (sharedAliases.length === 0) {
            return false;
        }

        if (sharedAliases.some((alias) => ReactiveCatalogSource.isSourceAwareAlias(alias))) {
            return true;
        }

        if (firstAliases.some((alias) => ReactiveCatalogSource.isSourceAwareAlias(alias)) ||
            secondAliases.some((alias) => ReactiveCatalogSource.isSourceAwareAlias(alias))) {
            return false;
        }

        return true;
    }

    private static isSourceAwareAlias(alias: string): boolean {
        return alias.includes(CATALOG_KEY_SEPARATOR);
    }

    private static addAliasCandidates(aliases: Set<string>, values: readonly unknown[]): void {
        for (const value of values) {
            for (const alias of ReactiveCatalogSource.toAliasCandidates(value)) {
                aliases.add(alias);
            }
        }
    }

    private static addSourceAliases<TSource extends ReactiveCatalogSourceName>(
        aliases: Set<string>,
        source: TSource,
        values: readonly unknown[],
    ): void {
        for (const value of values) {
            for (const alias of ReactiveCatalogSource.toAliasCandidates(value)) {
                aliases.add(ReactiveCatalogSource.buildKey(source, alias));
            }
        }
    }

    private static addCompositeAliases<TSource extends ReactiveCatalogSourceName>(
        aliases: Set<string>,
        source: TSource,
        primaryValues: readonly unknown[],
        secondaryValues: readonly unknown[],
    ): void {
        for (const primaryValue of primaryValues) {
            for (const primaryAlias of ReactiveCatalogSource.toAliasCandidates(primaryValue)) {
                for (const secondaryValue of secondaryValues) {
                    for (const secondaryAlias of ReactiveCatalogSource.toAliasCandidates(secondaryValue)) {
                        aliases.add(ReactiveCatalogSource.buildKey(source, primaryAlias, secondaryAlias));
                    }
                }
            }
        }
    }

    private static toAliasCandidates(value: unknown): string[] {
        const rawAlias = ReactiveCatalogSource.toStringId(value);

        if (rawAlias === '') {
            return [];
        }

        const normalizedAlias = ReactiveCatalogSource.normalizeName(rawAlias);

        return normalizedAlias !== '' && normalizedAlias !== rawAlias
            ? [rawAlias, normalizedAlias]
            : [rawAlias];
    }
}
